import React, { useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AnalyzerApiUrl } from '../utilities/constants';
import './AnalyticsDashboard.css';

// ── KPI Strip ──────────────────────────────────────────────────────────────

function KPICard({ label, value, valueColor }) {
  return (
    <Paper variant="outlined" className="kpi-card">
      <Typography variant="caption" className="kpi-label">{label}</Typography>
      <Typography variant="h5" className="kpi-value" sx={{ color: valueColor || '#0f0e2e' }}>
        {value}
      </Typography>
    </Paper>
  );
}

function KPIStrip({ user: u }) {
  const totalComplete = u?.total_jobs_complete ?? 0;
  const totalJobs = u?.total_jobs ?? 0;
  const successRate = totalJobs > 0 ? Math.round((u?.success_rate ?? 0) * 100) : 0;

  const preCompliant = u?.pre_compliance?.compliant ?? 0;
  const postCompliant = u?.post_compliance?.compliant ?? 0;
  const preNonCompliant = u?.pre_compliance?.non_compliant ?? 0;
  const complianceUplift = preNonCompliant > 0
    ? Math.round((Math.max(0, postCompliant - preCompliant) / preNonCompliant) * 100)
    : null;

  const figs = u?.total_figures_tagged ?? 0;
  const figsNoAlt = u?.total_figures_without_alt ?? 0;
  const altTextCoverage = (figs + figsNoAlt) > 0
    ? Math.round((figs / (figs + figsNoAlt)) * 100)
    : null;

  return (
    <div className="kpi-strip">
      <KPICard label="Your Jobs" value={totalJobs.toLocaleString()} />
      <KPICard label="Pages Remediated" value={(u?.total_pages_remediated ?? 0).toLocaleString()} />
      <KPICard label="Elements Tagged" value={(u?.total_elements_tagged ?? 0).toLocaleString()} />
      <KPICard
        label="Compliance Uplift"
        value={complianceUplift !== null ? `${complianceUplift > 0 ? '+' : ''}${complianceUplift}%` : '—'}
        valueColor={
          complianceUplift === null ? '#0f0e2e'
          : complianceUplift >= 50 ? '#16a34a'
          : complianceUplift >= 20 ? '#d97706'
          : '#dc2626'
        }
      />
      <KPICard
        label="Alt Text Coverage"
        value={altTextCoverage !== null ? `${altTextCoverage}%` : '—'}
        valueColor={
          altTextCoverage === null ? '#0f0e2e'
          : altTextCoverage >= 90 ? '#16a34a'
          : altTextCoverage >= 70 ? '#d97706'
          : '#dc2626'
        }
      />
      <KPICard
        label="Success Rate"
        value={totalJobs > 0 ? `${successRate}%` : '—'}
        valueColor={successRate >= 90 ? '#16a34a' : successRate >= 70 ? '#d97706' : successRate > 0 ? '#dc2626' : '#0f0e2e'}
      />
    </div>
  );
}

// ── Page Quota Gauge ───────────────────────────────────────────────────────

function PageQuotaGauge({ pageUsage }) {
  const consumed = pageUsage?.consumed ?? 0;
  const limit = pageUsage?.limit ?? 1;
  const pct = Math.min(100, Math.round((consumed / limit) * 100));
  const color = pct >= 90 ? '#dc2626' : pct >= 75 ? '#d97706' : '#16a34a';
  const deg = (pct / 100) * 360;

  return (
    <Paper variant="outlined" className="widget-card" component="section" aria-labelledby="page-quota-title">
      <Typography variant="subtitle2" className="widget-title" id="page-quota-title">Your Page Quota</Typography>
      <div className="gauge-container">
        <div
          className="gauge-ring"
          style={{
            background: `conic-gradient(${color} 0deg ${deg}deg, #e2e8f0 ${deg}deg 360deg)`,
          }}
          role="img"
          aria-label={`Page quota ${pct}% used: ${consumed.toLocaleString()} of ${limit.toLocaleString()} pages consumed.`}
        >
          <div className="gauge-inner" aria-hidden="true">
            <Typography variant="h5" sx={{ fontWeight: 800, color, lineHeight: 1 }}>{pct}%</Typography>
            <Typography variant="caption" sx={{ color: '#475569' }}>used</Typography>
          </div>
        </div>
        <Typography variant="body2" sx={{ color: '#475569' }}>
          {consumed.toLocaleString()} / {limit.toLocaleString()} pages
        </Typography>
      </div>
    </Paper>
  );
}

// ── Compliance Impact (per-user) ───────────────────────────────────────────

function ComplianceImpact({ user: u }) {
  const preCompliant = u?.pre_compliance?.compliant ?? 0;
  const preNonCompliant = u?.pre_compliance?.non_compliant ?? 0;
  const preTotal = preCompliant + preNonCompliant;
  const postCompliant = u?.post_compliance?.compliant ?? 0;
  const postNonCompliant = u?.post_compliance?.non_compliant ?? 0;
  const postTotal = postCompliant + postNonCompliant;
  const improved = Math.max(0, postCompliant - preCompliant);

  const preNonPct = preTotal > 0 ? Math.round((preNonCompliant / preTotal) * 100) : 0;
  const postPct = postTotal > 0 ? Math.round((postCompliant / postTotal) * 100) : 0;

  return (
    <Paper variant="outlined" className="widget-card" component="section" aria-labelledby="compliance-impact-title">
      <Typography variant="subtitle2" className="widget-title" id="compliance-impact-title">Your Compliance Impact</Typography>
      <div
        className="compliance-row"
        role="group"
        aria-label={`Before remediation: ${preNonCompliant.toLocaleString()} of ${preTotal.toLocaleString()} documents non-compliant (${preNonPct} percent). After remediation: ${postCompliant.toLocaleString()} of ${postTotal.toLocaleString()} documents compliant (${postPct} percent).`}
      >
        <div className="compliance-col">
          <Typography variant="caption" className="compliance-label">Before</Typography>
          <div className="compliance-badge noncompliant">
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#b91c1c', lineHeight: 1 }}>
              {preTotal > 0 ? `${preNonPct}%` : '—'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#b91c1c', fontWeight: 700, display: 'block' }}>
              NON-COMPLIANT
            </Typography>
          </div>
          <Typography variant="caption" sx={{ color: '#64748b', mt: 0.5, display: 'block' }}>
            {preNonCompliant.toLocaleString()} docs
          </Typography>
        </div>

        <Typography sx={{ color: '#475569', fontWeight: 700, fontSize: '1.4rem', mt: 2 }} aria-hidden="true">→</Typography>

        <div className="compliance-col">
          <Typography variant="caption" className="compliance-label">After</Typography>
          <div className="compliance-badge compliant">
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#15803d', lineHeight: 1 }}>
              {postTotal > 0 ? `${postPct}%` : '—'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#15803d', fontWeight: 700, display: 'block' }}>
              COMPLIANT
            </Typography>
          </div>
          <Typography variant="caption" sx={{ color: '#64748b', mt: 0.5, display: 'block' }}>
            {postCompliant.toLocaleString()} docs
          </Typography>
        </div>
      </div>

      {improved > 0 && (
        <div className="compliance-summary">
          <Typography variant="body2" sx={{ color: '#15803d', fontWeight: 700 }}>
            +{improved.toLocaleString()} document{improved === 1 ? '' : 's'} now compliant
          </Typography>
        </div>
      )}
    </Paper>
  );
}

// ── Loading skeleton ───────────────────────────────────────────────────────

function AnalyticsSkeleton() {
  return (
    <div className="analytics-root">
      <Skeleton variant="text" width={160} height={40} sx={{ mb: 1 }} />
      <div className="kpi-strip">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} variant="rounded" height={88} sx={{ borderRadius: '10px' }} />
        ))}
      </div>
      <div className="analytics-row">
        <Skeleton variant="rounded" height={220} sx={{ borderRadius: '10px' }} />
        <Skeleton variant="rounded" height={220} sx={{ borderRadius: '10px' }} />
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function AnalyticsDashboard({ idToken }) {
  const [state, setState] = useState({ data: null, loading: false, error: null });
  const retryRef = useRef(null);

  const fetchAnalytics = useCallback(async () => {
    if (!idToken) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const resp = await fetch(`${AnalyzerApiUrl}/analytics`, {
        headers: { Authorization: idToken },
      });
      if (!resp.ok) throw new Error(`Analytics API returned ${resp.status}`);
      const data = await resp.json();
      setState({ data, loading: false, error: null });
      if (data.backfill_in_progress) {
        retryRef.current = setTimeout(fetchAnalytics, 5000);
      }
    } catch (err) {
      if (retryRef.current) clearTimeout(retryRef.current);
      setState({ data: null, loading: false, error: err.message });
    }
  }, [idToken]);

  useEffect(() => {
    fetchAnalytics();
    return () => {
      if (retryRef.current) clearTimeout(retryRef.current);
    };
  }, [fetchAnalytics]);

  const { data, loading, error } = state;

  if (loading && !data) return <AnalyticsSkeleton />;

  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert
          severity="error"
          action={<Button size="small" onClick={fetchAnalytics}>Retry</Button>}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <div className="analytics-root">
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography
          component="h1"
          variant="h5"
          sx={{ fontWeight: 700, color: '#0f0e2e', flexGrow: 1 }}
        >
          Your Analytics
        </Typography>
        <Tooltip title="Refresh analytics">
          <IconButton
            onClick={fetchAnalytics}
            aria-label="Refresh analytics"
            size="small"
            disabled={loading}
          >
            {loading
              ? <CircularProgress size={20} aria-label="Refreshing" />
              : <RefreshIcon aria-hidden="true" />}
          </IconButton>
        </Tooltip>
      </Box>

      {data?.backfill_in_progress && (
        <Alert severity="info">
          Compiling your analytics from historical jobs. Refreshing in 5s…
        </Alert>
      )}

      <KPIStrip user={data?.user} />

      <div className="analytics-row">
        <PageQuotaGauge pageUsage={data?.page_usage} />
        <ComplianceImpact user={data?.user} />
      </div>
    </div>
  );
}
